/**
 * Workspace Persistence Utility
 *
 * Provides localStorage persistence for workspace state across browser sessions.
 * Follows the same pattern as puppetStore.ts persist middleware.
 *
 * Features:
 * - Manual save with schema versioning
 * - Storage availability detection (handles private browsing)
 * - Storage quota monitoring with 80% warning threshold
 * - State extraction helpers that exclude transient fields
 */

import {
  useSceneStore,
  type Tab,
  type SceneMetadata,
} from '../stores/sceneStore';
import {
  useTimelineStore,
  type Track,
  type Tool,
  type Group,
  type PlacedGroup,
} from '../stores/timelineStore';
import { useUiStore } from '../stores/uiStore';
import { usePuppetStore, type PuppetConfig } from '../stores/puppetStore';

// Storage constants
export const WORKSPACE_STORAGE_KEY = 'puppet-studio-workspace';
export const WORKSPACE_SCHEMA_VERSION = 4; // v4: Added puppetConfig support

// 5MB localStorage limit (standard browser limit)
const STORAGE_LIMIT_BYTES = 5 * 1024 * 1024;
// Warning threshold at 80% of limit
const STORAGE_WARNING_THRESHOLD = 0.8;

/**
 * Persisted scene state (excludes isDirty, lastSavedAt which are session-specific)
 */
export interface PersistedSceneState {
  tabs: Tab[];
  activeTabId: string | null;
  metadata: SceneMetadata;
}

/**
 * Persisted timeline state (excludes playback state)
 */
export interface PersistedTimelineState {
  tracks: Track[];
  tools: Tool[];
  duration: number;
  groups: Group[];
  placedGroups: PlacedGroup[];
  // Excluded: playheadPosition, isPlaying, playbackSpeed
}

/**
 * Persisted UI state (excludes transient selection)
 */
export interface PersistedUiState {
  sidebarWidth: number;
  previewHeight: number;
  timelineHeight: number;
  pixelsPerSecond: number;
  configuratorOpen: boolean;
  // Excluded: selectedToolBlockId, cameraPosition (per-session)
}

/**
 * Persisted puppet config state (motor settings, positions, bone mapping)
 */
export interface PersistedPuppetConfig {
  activePuppet: PuppetConfig | null;
}

/**
 * Complete workspace schema with version for migrations
 */
export interface WorkspaceSchema {
  version: number;
  sceneStore: PersistedSceneState;
  timelineStore: PersistedTimelineState;
  uiStore: PersistedUiState;
  puppetConfig?: PersistedPuppetConfig;
  savedAt: string;
}

/**
 * Result of save operation
 */
export interface SaveResult {
  success: boolean;
  error?: string;
}

/**
 * Result of restore operation
 */
export interface RestoreResult {
  success: boolean;
  restored: boolean;
  error?: string;
}

/**
 * Storage quota status
 */
export interface StorageQuotaStatus {
  used: number;
  limit: number;
  percentage: number;
}

/**
 * Check if localStorage is available
 *
 * Handles private browsing mode and disabled localStorage scenarios.
 * Uses a test write/read/delete cycle to verify actual availability.
 *
 * @returns true if localStorage is available and functional
 */
export function isLocalStorageAvailable(): boolean {
  const testKey = '__puppet_studio_test__';
  try {
    localStorage.setItem(testKey, 'test');
    const result = localStorage.getItem(testKey);
    localStorage.removeItem(testKey);
    return result === 'test';
  } catch {
    return false;
  }
}

/**
 * Calculate the size of data in bytes when stored as JSON
 *
 * @param data - Object to calculate size for
 * @returns Size in bytes (UTF-16 encoding, 2 bytes per char)
 */
export function calculateStorageSize(data: object): number {
  const json = JSON.stringify(data);
  // localStorage uses UTF-16, so each character is 2 bytes
  return json.length * 2;
}

/**
 * Get current localStorage quota status
 *
 * @returns Object with used bytes, limit bytes, and percentage used
 */
export function getStorageQuotaStatus(): StorageQuotaStatus {
  let used = 0;

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value) {
          // UTF-16: 2 bytes per character for both key and value
          used += (key.length + value.length) * 2;
        }
      }
    }
  } catch {
    // If we can't enumerate, estimate from our key only
    const ourData = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (ourData) {
      used = (WORKSPACE_STORAGE_KEY.length + ourData.length) * 2;
    }
  }

  return {
    used,
    limit: STORAGE_LIMIT_BYTES,
    percentage: used / STORAGE_LIMIT_BYTES,
  };
}

/**
 * Check if storage is near capacity (>80% full)
 *
 * @returns true if storage usage exceeds warning threshold
 */
export function isStorageNearCapacity(): boolean {
  const status = getStorageQuotaStatus();
  return status.percentage >= STORAGE_WARNING_THRESHOLD;
}

/**
 * Extract persistable state from sceneStore
 * Excludes isDirty and lastSavedAt (session-specific tracking fields)
 */
export function extractSceneState(): PersistedSceneState {
  const state = useSceneStore.getState();
  return {
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    metadata: state.metadata,
  };
}

/**
 * Extract persistable state from timelineStore
 * Excludes playback state (playheadPosition, isPlaying, playbackSpeed)
 */
export function extractTimelineState(): PersistedTimelineState {
  const state = useTimelineStore.getState();
  return {
    tracks: state.tracks,
    tools: state.tools,
    duration: state.duration,
    groups: state.groups,
    placedGroups: state.placedGroups,
  };
}

/**
 * Extract persistable state from uiStore
 * Excludes selectedToolBlockId (transient selection) and cameraPosition (per-session)
 */
export function extractUiState(): PersistedUiState {
  const state = useUiStore.getState();
  return {
    sidebarWidth: state.sidebarWidth,
    previewHeight: state.previewHeight,
    timelineHeight: state.timelineHeight,
    pixelsPerSecond: state.pixelsPerSecond,
    configuratorOpen: state.configuratorOpen,
  };
}

/**
 * Extract persistable state from puppetStore
 * Includes motor configs with axis, boneOffset, direction, xOffset, zOffset
 */
export function extractPuppetConfig(): PersistedPuppetConfig {
  const state = usePuppetStore.getState();
  return {
    activePuppet: state.activePuppet,
  };
}

/**
 * Apply persisted scene state to sceneStore
 */
export function applySceneState(data: PersistedSceneState): void {
  useSceneStore.setState((state) => ({
    ...state,
    tabs: data.tabs ?? state.tabs,
    activeTabId: data.activeTabId ?? state.activeTabId,
    metadata: data.metadata ?? state.metadata,
    isDirty: false, // Reset dirty flag after restore
    lastSavedAt: new Date().toISOString(),
  }));
}

/**
 * Apply persisted timeline state to timelineStore
 */
export function applyTimelineState(data: PersistedTimelineState): void {
  useTimelineStore.setState((state) => ({
    ...state,
    tracks: data.tracks ?? state.tracks,
    tools: data.tools ?? state.tools,
    duration: data.duration ?? state.duration,
    groups: data.groups ?? state.groups,
    placedGroups: data.placedGroups ?? [],
    // Reset playback state to defaults
    playheadPosition: 0,
    isPlaying: false,
    playbackSpeed: 1.0,
  }));
}

/**
 * Apply persisted UI state to uiStore
 */
export function applyUiState(data: PersistedUiState): void {
  useUiStore.setState((state) => ({
    ...state,
    sidebarWidth: data.sidebarWidth ?? state.sidebarWidth,
    previewHeight: data.previewHeight ?? state.previewHeight,
    timelineHeight: data.timelineHeight ?? state.timelineHeight,
    pixelsPerSecond: data.pixelsPerSecond ?? state.pixelsPerSecond,
    configuratorOpen: data.configuratorOpen ?? false,
    // Reset transient state
    selectedToolBlockId: null,
  }));
}

/**
 * Apply persisted puppet config to puppetStore
 */
export function applyPuppetConfig(data: PersistedPuppetConfig): void {
  if (data.activePuppet) {
    usePuppetStore.setState((state) => ({
      ...state,
      activePuppet: data.activePuppet,
    }));
  }
}

/**
 * Migrate workspace data from older schema versions
 *
 * @param data - Raw persisted data
 * @returns Migrated data compatible with current schema
 */
function migrateWorkspaceData(data: WorkspaceSchema): WorkspaceSchema {
  const migrated = { ...data };

  // Version 0 to 1: Initial schema, no migrations needed yet
  if (migrated.version === 0) {
    migrated.version = 1;
  }

  // Version 1 to 2: Add groups field to timelineStore
  if (migrated.version === 1) {
    migrated.timelineStore = {
      ...migrated.timelineStore,
      groups: [], // Default to empty groups array
    };
    migrated.version = 2;
  }

  // Version 2 to 3: Add placedGroups field to timelineStore
  if (migrated.version === 2) {
    migrated.timelineStore = {
      ...migrated.timelineStore,
      placedGroups: [], // Default to empty placedGroups array
    };
    migrated.version = 3;
  }

  // Version 3 to 4: Add puppetConfig field
  if (migrated.version === 3) {
    // puppetConfig is optional, will be undefined for old saves
    // and will be populated from puppetStore's own persistence
    migrated.version = 4;
  }

  return migrated;
}

/**
 * Save current workspace state to localStorage
 *
 * Gathers state from sceneStore, timelineStore, and uiStore,
 * adds schema version, and saves to localStorage.
 *
 * @returns Result object with success status and optional error message
 */
export function saveWorkspace(): SaveResult {
  if (!isLocalStorageAvailable()) {
    return {
      success: false,
      error:
        'localStorage is not available. You may be in private browsing mode.',
    };
  }

  try {
    const workspace: WorkspaceSchema = {
      version: WORKSPACE_SCHEMA_VERSION,
      sceneStore: extractSceneState(),
      timelineStore: extractTimelineState(),
      uiStore: extractUiState(),
      puppetConfig: extractPuppetConfig(),
      savedAt: new Date().toISOString(),
    };

    const json = JSON.stringify(workspace);

    // Check if save would exceed quota
    const dataSize = json.length * 2;
    const currentStatus = getStorageQuotaStatus();
    const existingSize =
      localStorage.getItem(WORKSPACE_STORAGE_KEY)?.length ?? 0;
    const netIncrease = dataSize - existingSize * 2;

    if (currentStatus.used + netIncrease > STORAGE_LIMIT_BYTES) {
      return {
        success: false,
        error: 'Storage limit reached. Export your work to save.',
      };
    }

    localStorage.setItem(WORKSPACE_STORAGE_KEY, json);

    // Mark scene as clean after successful save
    useSceneStore.getState().markClean();

    return { success: true };
  } catch (error) {
    // Handle quota exceeded error specifically
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      return {
        success: false,
        error: 'Storage limit reached. Export your work to save.',
      };
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Restore workspace state from localStorage
 *
 * Checks for existing saved data, validates schema version,
 * runs migrations if needed, and applies state to stores.
 *
 * @returns Result object with success status, whether data was restored, and optional error
 */
export function restoreWorkspace(): RestoreResult {
  if (!isLocalStorageAvailable()) {
    return {
      success: false,
      restored: false,
      error: 'localStorage is not available.',
    };
  }

  try {
    const saved = localStorage.getItem(WORKSPACE_STORAGE_KEY);

    if (!saved) {
      return {
        success: true,
        restored: false,
      };
    }

    let workspace: WorkspaceSchema;

    try {
      workspace = JSON.parse(saved) as WorkspaceSchema;
    } catch {
      return {
        success: false,
        restored: false,
        error: 'Saved data is corrupted.',
      };
    }

    // Migrate if needed
    if (workspace.version !== WORKSPACE_SCHEMA_VERSION) {
      workspace = migrateWorkspaceData(workspace);
    }

    // Apply state to stores
    applySceneState(workspace.sceneStore);
    applyTimelineState(workspace.timelineStore);
    applyUiState(workspace.uiStore);
    if (workspace.puppetConfig) {
      applyPuppetConfig(workspace.puppetConfig);
    }

    return {
      success: true,
      restored: true,
    };
  } catch (error) {
    return {
      success: false,
      restored: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Clear saved workspace from localStorage
 */
export function clearSavedWorkspace(): void {
  try {
    localStorage.removeItem(WORKSPACE_STORAGE_KEY);
  } catch {
    // Ignore errors when clearing
  }
}

/**
 * Initialize workspace by restoring from localStorage if available.
 * Called on module load to restore state before first render.
 *
 * Uses setTimeout to ensure Zustand stores are fully initialized before
 * attempting to restore state.
 */
export function initializeWorkspace(): void {
  // Use setTimeout to ensure stores are initialized (same pattern as puppetStore)
  setTimeout(() => {
    const result = restoreWorkspace();
    if (result.restored) {
      console.log(
        '[WorkspacePersistence] Restored workspace from localStorage'
      );
    }
  }, 0);
}

// Auto-initialize on module load
// This runs when the module is first imported
initializeWorkspace();
